import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

export default function Layout() {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({ admin: true });
  const location = useLocation();

  const isSuperAdmin = user?.role === 'superadmin';
  const isHolding = user?.account_type === 'holding';

  const toggleSection = useCallback((section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Åbn admin-sektionen automatisk hvis man er på en admin-side
  const adminPaths = ['/app/kontoplan', '/app/aabningsbalance', '/app/eksport', '/app/aendringslog', '/app/import', '/app/indstillinger'];

  useEffect(() => {
    setSidebarOpen(false);
    if (adminPaths.some(p => location.pathname.startsWith(p))) {
      setCollapsedSections(prev => ({ ...prev, admin: false }));
    }
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  return (
    <div className="app-layout">
      <div className="mobile-header">
        <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Menu">
          <span className={`hamburger-icon ${sidebarOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        <span className="mobile-title">{brand.name}</span>
      </div>

      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h2>{brand.name}</h2>
          <div className="cvr">{isSuperAdmin ? 'Superadministrator' : (user?.association_name || '')}</div>
        </div>
        <nav className="sidebar-nav">
          {isSuperAdmin ? (
            <>
              <NavLink to="/app/superadmin">Foreningsoversigt</NavLink>
              <NavLink to="/app/indstillinger">Indstillinger</NavLink>
            </>
          ) : (
            <>
              <NavLink to="/app" end>Oversigt</NavLink>
              <NavLink to="/app/kassekladde">Kassekladde</NavLink>
              <NavLink to="/app/hurtig">Hurtig bogføring</NavLink>
              <NavLink to="/app/bilag">Bilag</NavLink>
              <NavLink to="/app/fakturaer">Fakturaer</NavLink>
              <NavLink to="/app/posteringer">Posteringer</NavLink>
              {!isHolding && <NavLink to="/app/kontingent">Kontingent</NavLink>}
              <NavLink to="/app/resultat">Resultatopgørelse</NavLink>
              <NavLink to="/app/balance">Balance</NavLink>
              {!isHolding && <NavLink to="/app/medlemmer">Medlemsliste</NavLink>}
              {!isHolding && <NavLink to="/app/aktiviteter">Aktiviteter</NavLink>}
              <NavLink to="/app/bankimport">Bankimport</NavLink>

              <div className="nav-section nav-section-toggle" onClick={() => toggleSection('admin')}>
                Administration
                <span className={`nav-chevron ${collapsedSections.admin ? '' : 'nav-chevron-open'}`}>&#9656;</span>
              </div>
              {!collapsedSections.admin && (
                <>
                  <NavLink to="/app/kontoplan">Kontoplan</NavLink>
                  <NavLink to="/app/aabningsbalance">Åbningsbalance</NavLink>
                  <NavLink to="/app/eksport">Eksport &amp; backup</NavLink>
                  <NavLink to="/app/aendringslog">Ændringslog</NavLink>
                  <NavLink to="/app/import">Importer data</NavLink>
                  <NavLink to="/app/indstillinger">Indstillinger</NavLink>
                </>
              )}
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-name">{user?.name}</div>
          <button onClick={logout}>Log ud</button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
